import { getLocalValue, removeLocalValue } from "../libs/localStorage";
import { LogoInfo } from "../types/logo";
import request, { authRequest } from "./../libs/request";

export type TokenType = {
  grantType: string;
  accessToken: string;
  refreshToken: string;
  auth: string;
};

export type LoginRequest = {
  username: string;
  password: string;
  clientId: string;
};

export const login = (params: LoginRequest) =>
  authRequest.post<TokenType>("oauth2/token", params);

export const logout = async () => {
  let longTermTokens: SignOutLongTermRequest[] = [];
  if (getLocalValue("AUTO")) {
    longTermTokens.push({
      scope: "AUTO",
      token: getLocalValue("AUTO"),
    } as SignOutLongTermRequest);
  }
  try {
    signOut({ longTermTokens });
  } finally {
    removeLocalValue("jwtToken");
    removeLocalValue("AUTO");
    window.location.href = "/auth/login";
  }
};

export type ScopeType = "BIO" | "AUTO";

export type ScopeRequest = {
  scope: ScopeType;
};

export type BearerAccessTokenRequest = {
  accessToken: string;
};

export type getLongTermTokenResponse = {
  grantType: string;
  longTermToken: string;
};

export const getLongTermToken = (
  params: BearerAccessTokenRequest,
  scope: ScopeRequest
) =>
  authRequest.post<getLongTermTokenResponse>("oauth2/token/long-term", scope, {
    headers: { Authorization: params.accessToken },
  });

export type DeleteLongTermRequest = {
  Scope: ScopeType;
  Token: string;
};

export const deleteLongTermToken = (headers: DeleteLongTermRequest) =>
  request.delete(`${process.env.REACT_APP_API_URL}/oauth2/token/long-term`, {
    headers,
  });

export type FindIdRequest = {
  name: string;
  mobileNumber: string;
  memberCi: string;
};

export const findId = (params: FindIdRequest) =>
  authRequest.post(`v2/user/findId`, params);

export type EmailRequest = {
  email: string;
};

export const checkEmailDuplicate = (params: EmailRequest) =>
  authRequest.get("v2/user/email/duplicated", { params });

export const checkReferrerEmailExistence = (params: EmailRequest) =>
  authRequest.get("/v2/user/referrer/existence", { params });

export const sendAuthCodeByEmail = (params: EmailRequest) =>
  authRequest.post("v2/user/email/auth-code", params);

export type EmailAuth = EmailRequest & {
  authCode: string;
};

export const checkAuthCodeByEmail = (params: EmailAuth) =>
  authRequest.get("v2/user/email/auth-code/check", { params });

export type JoinRequest = {
  name: string;
  nickname: string;
  email: string;
  referrerEmail?: string;
  password: string;
  mobileNumber: string;
  memberCi: string;
  advertise: boolean;
  gender: string;
  birthday: string;
  marketing?: boolean;
};

export const join = (params: JoinRequest) =>
  authRequest.post("v2/user/join", params);

export type Nation =
  | "0" // 내국인
  | "1"; // 외국인

export type SendCertRequest = {
  name: string;
  mdn: string;
  telco: string;
  birth: string;
  gender: string;
  nation: Nation;
};

export type SendCertResponse = {
  result: number;
  reqNo: string;
  resultCd: string;
};

export const sendCert = (params: SendCertRequest) =>
  authRequest.post<SendCertResponse>("v2/user/cert/send", params);

export type CheckCertRequest = SendCertRequest & {
  reqNo: string;
  authnum: string;
};

export type CheckCertResponse = {
  result: string;
  resultCd: string;
  ci: string;
  di: string;
};

export const checkCert = (params: CheckCertRequest) =>
  authRequest.post<CheckCertResponse>("v2/user/cert/check", params);

export type ResetPasswordRequest = () => {
  name: string;
  mobileNumber: string;
  memberCi: string;
  password: string;
};

export const resetPassword = (params: ResetPasswordRequest) =>
  authRequest.post("v2/user/reset-password", params);

export const checkNicknameDuplicate = (nickname: string) =>
  authRequest.get("v2/user/nickname/duplicated", { params: { nickname } });

export const checkMobileNumberDuplicate = (mobileNumber: string) =>
  authRequest.get("v2/user/mobile-number/duplicated", {
    params: { mobileNumber },
  });

export const checkCiDuplicate = (ci: string) =>
  authRequest.get("v2/user/ci/duplicated", { params: { memberCi: ci } });

export type SignOutLongTermRequest = {
  scope: ScopeType;
  token: string;
};

export type SignOutRequest = {
  longTermTokens: SignOutLongTermRequest[];
};

export const signOut = (params: SignOutRequest) =>
  request.post(`${process.env.REACT_APP_API_URL}/oauth2/sign-out`, params);

export type MaintenanceResponse = {
  isMaintenance: boolean;
  message?: string;
};

export const getMaintenanceFlag = () =>
  request.get<MaintenanceResponse>("/maintenance");

export const getUserLogoInfo = () => request.get<LogoInfo>("/member/me/logo");
